import { FileText } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { OrigemBadge } from '@/components/ui/OrigemBadge';

import { TagsDoRegistro } from '@/components/tags/TagsDoRegistro';
import { useTagsNoCache } from '@/hooks/useTagsNoCache';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { ModeloActionsMenu } from './ModeloActionsMenu';
import type { PeticaoModelo } from './ModelosPage';
import { extractPlainText } from './ModelosPage';

interface ModeloGridProps {
  modelos: PeticaoModelo[];
  canWrite: (origem?: string | null, escritorioId?: string | null) => boolean;
  canShare: (origem?: string | null, escritorioId?: string | null, userId?: string | null) => boolean;
  onUsar: (modelo: PeticaoModelo) => void;
  onView: (modelo: PeticaoModelo) => void;
  onEdit: (modelo: PeticaoModelo) => void;
  onDuplicate: (modelo: PeticaoModelo) => void;
  onShare: (modelo: PeticaoModelo) => void;
  onDelete: (modelo: PeticaoModelo) => void;
  /** O modelo cujo diálogo de tags está aberto (nulo ao fechar). */
  onTagsAbertas?: (id: string | null) => void;
}

const CACHES_DE_MODELO = [queryKeys.modelos.all] as const;

export function ModeloGrid({
  modelos,
  canWrite,
  canShare,
  onUsar,
  onView,
  onEdit,
  onDuplicate,
  onShare,
  onDelete,
  onTagsAbertas,
}: ModeloGridProps) {
  const tagsNoCache = useTagsNoCache(CACHES_DE_MODELO);
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
      {modelos.map((modelo) => (
        <Card
          key={modelo.id}
          className="flex h-full cursor-pointer flex-col border bg-card transition-colors hover:border-primary/30"
          onClick={() => onView(modelo)}
        >
          <CardContent className="flex flex-1 flex-col p-6">
            <div className="mb-4 flex items-start justify-between">
              <div className="flex min-w-0 flex-1 items-center gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <FileText className="h-4 w-4" />
                </div>
                <p className="min-w-0 break-words text-base font-semibold">
                  {modelo.titulo}
                </p>
              </div>
              <TagsDoRegistro
                recurso="modelos"
                registroId={modelo.id}
                identificador={modelo.titulo}
                tags={modelo.tags}
                noCache={tagsNoCache}
                onAbertoChange={(aberto) => onTagsAbertas?.(aberto ? modelo.id : null)}
                max={2}
                className="shrink-0"
              />
              <ModeloActionsMenu
                modelo={modelo}
                canWrite={canWrite}
                canShare={canShare}
                onUsar={onUsar}
                onView={onView}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onShare={onShare}
                onDelete={onDelete}
                triggerClassName="shrink-0 text-muted-foreground"
                stopPropagation
              />
            </div>

            <div className="flex-1 rounded bg-muted/30 p-4 overflow-hidden">
              <p className="line-clamp-4 text-sm leading-relaxed text-muted-foreground">
                {extractPlainText(modelo.corpo) || 'Modelo sem conteúdo'}
              </p>
            </div>

            {modelo.created_at && (
              <span className="mt-4 text-xs text-muted-foreground">
                Criado em {new Date(modelo.created_at).toLocaleDateString('pt-BR')}
              </span>
            )}
            {modelo.origem && modelo.origem !== 'pessoal' && (
              <div className="mt-2">
                <OrigemBadge item={modelo} />
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
